import { Block, BlockFooter, Link, LoginScreenTitle, Page } from "framework7-react";
import { useCallback, useMemo, useState } from "react";

import { PasswordResetForm } from "@/components/PasswordResetForm";
import { PasswordResetRequestForm } from "@/components/PasswordResetRequestForm";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { usePageReady } from "@/hooks/usePageReady";

export const PasswordResetPage = createPage("PasswordResetPage", PageComponent);

function PageComponent({ pageName, f7route, f7router }: PageComponentProps) {
  const { getPageProps } = usePageReady(pageName);
  const [requested, setRequested] = useState(false);

  const { authHeaders, hasTokenParam, resetToken } = useMemo(() => {
    const normalizeQuery = (value: unknown) => (Array.isArray(value) ? value[0] : value);
    const rawToken = f7route.query?.reset_password_token;
    const normalized = normalizeQuery(rawToken);
    const accessToken = normalizeQuery(f7route.query?.["access-token"]);
    const client = normalizeQuery(f7route.query?.client);
    const uid = normalizeQuery(f7route.query?.uid);
    const hasAuthHeaders = typeof accessToken === "string" && typeof client === "string" && typeof uid === "string";
    return {
      authHeaders: hasAuthHeaders
        ? { accessToken: accessToken.trim(), client: client.trim(), uid: uid.trim() }
        : undefined,
      hasTokenParam: rawToken !== undefined,
      resetToken: typeof normalized === "string" ? normalized.trim() : "",
    };
  }, [f7route.query]);

  const redirectUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }
    return new URL("/password-reset/", window.location.origin).toString();
  }, []);
  const redirectToSignIn = useCallback(() => {
    if (typeof window !== "undefined") {
      window.location.href = "/sign-in/";
      return;
    }
    f7router.navigate("/sign-in/");
  }, [f7router]);

  return (
    <Page
      loginScreen
      noNavbar
      noSwipeback
      noToolbar
      data-component={pageName}
      {...getPageProps()}
    >
      <LoginScreenTitle>パスワード再設定</LoginScreenTitle>
      {hasTokenParam || authHeaders ? (
        resetToken || authHeaders ? (
          <PasswordResetForm
            authHeaders={authHeaders}
            resetToken={resetToken}
            onCompleted={redirectToSignIn}
          />
        ) : (
          <Block
            inset
            strong
          >
            リセットトークンがありません。再設定メールを再送してください。
          </Block>
        )
      ) : requested ? (
        <Block
          inset
          strong
        >
          再設定メールを送信しました。メール内のリンクからパスワードを再設定してください。
        </Block>
      ) : (
        <PasswordResetRequestForm
          redirectUrl={redirectUrl}
          onCompleted={() => setRequested(true)}
        />
      )}
      <Block>
        <BlockFooter>
          <Link href="/sign-in/">ログイン画面へ戻る</Link>
        </BlockFooter>
      </Block>
    </Page>
  );
}
